import { motion } from "framer-motion";
import { Award } from "lucide-react";
import { TEAM } from "../../data/content";

const EASE = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };

const LeadershipPattern = () => (
  <svg aria-hidden="true" data-testid="leadership-pattern" className="w-full h-full text-[#24589A]" viewBox="0 0 1200 700" preserveAspectRatio="none">
    <defs>
      <pattern id="leadership-cad-grid" width="72" height="72" patternUnits="userSpaceOnUse">
        <path d="M 72 0 L 0 0 0 72" fill="none" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="1200" height="700" fill="url(#leadership-cad-grid)" />
    <g fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M140 560 H400 V420 H620 V560 H900 V360 H1080" />
      <path d="M180 170 H450 V290 H720 V170 H990" />
      <circle cx="450" cy="290" r="38" />
      <circle cx="990" cy="170" r="46" />
    </g>
  </svg>
);

const initials = (name) => name.split(" ").map((part) => part[0]).slice(0, 2).join("").toUpperCase();

const LeaderCard = ({ member, index }) => (
  <motion.article
    data-testid={`team-card-${index}`}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={VIEWPORT}
    transition={{ duration: 0.62, delay: 0.18 + index * 0.12, ease: EASE }}
    whileHover={{ y: -4 }}
    className="group relative h-full overflow-hidden rounded-[10px] border border-[#E5E8EA] bg-white/[0.78] p-8 md:p-10 shadow-[0_14px_40px_rgba(19,38,58,0.06)] backdrop-blur-sm transition-[background-color,border-color,box-shadow] duration-300 hover:border-[#F47721]/35 hover:bg-white hover:shadow-[0_20px_50px_rgba(19,38,58,0.09)]"
  >
    <span aria-hidden="true" className="absolute left-0 top-0 h-full w-[3px] origin-top scale-y-0 bg-[#F47721] transition-transform duration-300 group-hover:scale-y-100" />
    <div className="flex items-start justify-between gap-6">
      <div className="flex items-center gap-5">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-[#F47721]/25 bg-[#F47721]/10 font-manrope text-xl font-bold text-[#F47721]">
          {initials(member.name)}
        </span>
        <div>
          <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.24em] text-[#F47721]">{member.role}</p>
          <h3 className="font-manrope font-bold tracking-tight text-[#17232D] text-[28px] md:text-[32px] leading-tight mt-2">
            {member.name}
          </h3>
        </div>
      </div>
      <span className="hidden sm:inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full border border-[#E5E8EA] bg-[#F7F7F4] px-3 py-2 font-inter text-[10px] font-semibold uppercase tracking-[0.14em] text-[#13263A]">
        <Award size={13} className="text-[#F47721]" /> {member.exp}
      </span>
    </div>

    <p className="font-inter text-[#59636D] text-[15px] md:text-base leading-[1.72] mt-7 max-w-xl">{member.bio}</p>

    <div className="mt-7 flex flex-wrap gap-2" data-testid={`leader-focus-${index}`}>
      <span className="rounded-full bg-[#13263A] px-3 py-1.5 font-inter text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
        {member.credential}
      </span>
      {member.focus.map((item) => (
        <span key={item} className="rounded-full border border-[#E5E8EA] bg-[#F7F7F4] px-3 py-1.5 font-inter text-[10px] font-semibold uppercase tracking-[0.12em] text-[#13263A]">
          {item}
        </span>
      ))}
    </div>
  </motion.article>
);

export const Leadership = () => (
  <section id="leadership" data-testid="leadership-section" className="relative overflow-hidden bg-[#F7F7F4] py-24 lg:py-32 border-y border-[#E5E8EA]">
    <motion.div
      aria-hidden="true"
      className="absolute inset-[-8%] opacity-[0.04] pointer-events-none"
      animate={{ x: [-22, 22, -22], y: [-8, 8, -8] }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
    >
      <LeadershipPattern />
    </motion.div>

    <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={VIEWPORT}
        transition={{ duration: 0.6, ease: EASE }}
        className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8"
      >
        <div className="max-w-4xl">
          <div className="flex items-center gap-4">
            <span className="h-[2px] w-10 bg-[#F47721]" />
            <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.32em] text-[#F47721]" data-testid="leadership-label">
              Leadership
            </p>
          </div>
          <h2 className="font-manrope font-bold tracking-tight leading-[1.06] text-[42px] md:text-[52px] lg:text-[56px] mt-6" data-testid="leadership-heading">
            <span className="block text-[#13263A]">EXPERIENCED LEADERSHIP.</span>
            <span className="block text-[#F47721]">ACCOUNTABLE DELIVERY.</span>
          </h2>
        </div>
        <p className="font-inter text-[#59636D] text-base md:text-[17px] leading-[1.65] max-w-md" data-testid="leadership-description">
          Decades of combined expertise across engineering, project management, design, and operations — committed to quality, integrity, and customer satisfaction.
        </p>
      </motion.div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="leadership-grid">
        {TEAM.map((member, i) => (
          <LeaderCard key={member.name} member={member} index={i} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={VIEWPORT}
        transition={{ duration: 0.55, delay: 0.2, ease: EASE }}
        className="mt-10 border-t border-[#E5E8EA] pt-7"
        data-testid="leadership-commitment"
      >
        <p className="font-inter text-[#59636D] text-sm md:text-base leading-[1.7] max-w-4xl">
          Supported by a multidisciplinary design and engineering team, dedicated site supervisors, quality-focused subcontractors, and a skilled execution workforce — engineering excellence at every level.
        </p>
      </motion.div>
    </div>
  </section>
);
